import {
  type AutocompleteRenderInputParams,
  Box,
  type SxProps,
  TextField,
  type Theme,
  Typography,
} from '@mui/material';
import LogRocket from 'logrocket';
import type { HTMLAttributes, ReactElement } from 'react';

import Enums from '@core/types/api/Enums';
import type { LookupInputOption } from '@core/utils/formParsingHelpers';
import type { NestedKeyOf } from '@core/utils/types';
import { randomUUID } from '@core/utils/uuid';

import type { FilterByOpts, ListURLParams, QueryRouteBarrelTypes } from '../../utils/ApiClient';
import { InputTooltip } from '../InputTooltip/InputTooltip';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';

export const DEFAULT_LOOKUP_LENGTH = 100;

/**
 * Helper type to extract the entity type from a barrel's ListURLParams.
 * Used to infer the type parameter for ConstructListQueryParams from the barrel.
 */
export type ExtractEntityTypeFromBarrel<
  QKey extends keyof QueryRouteBarrelTypes,
  QBarrel extends QueryRouteBarrelTypes[QKey] = QueryRouteBarrelTypes[QKey],
> = QBarrel['args'] extends { queryParams?: ListURLParams<infer T> }
  ? T
  : QBarrel['args'] extends { queryParams: ListURLParams<infer T> }
    ? T
    : {};

/**
 * Helper to create route args with automatic type inference from the barrel.
 * Use this to avoid explicitly specifying types in ConstructListQueryParams.
 *
 * @example
 * route: {
 *   barrel: 'GET divisions',
 *   args: makeRouteArgs((inputText) => ConstructListQueryParams(inputText)),
 * }
 */
export const makeRouteArgs =
  <QKey extends keyof QueryRouteBarrelTypes>(
    fn: (inputText: string) => QueryParams<ExtractEntityTypeFromBarrel<QKey>>,
  ) =>
  (inputText: string): QueryParams<ExtractEntityTypeFromBarrel<QKey>> =>
    fn(inputText);

// Re-export LookupInputOption from slab-core for backward compatibility
export type { LookupInputOption };

export type SharedLookupInputProps<
  FState extends Record<string, any>,
  FPath extends NestedKeyOf<FState>,
> = {
  formState: FState;
  name: FPath;
  label?: string;
  options: LookupInputOption[];

  /** @default false */
  grouped?: boolean;

  /**
   * onMatchChange is an optional callback that is invoked when the input
   * matches or does not match one of the component's supplied options.
   *
   * onMatchChange will be called with `null` if the input value does not
   * match any values in the passed in `options` parameter. If there is a match,
   * it will be called with the `option.value`.
   *
   * Currently, onMatchChange is invoked immediately prior to touching and
   * setting the value of the Formik field, so within the callback, the previous
   * value is still accessible in Formik's state while the new value is passed
   * to the callback.
   */
  onMatchChange?: (arg0: string | null) => void;

  /** @default empty */
  placeholder?: string;

  /** @default undefined */
  disabled?: boolean;
  tip?: string;
  sx?: SxProps<Theme>;
  readOnly?: boolean;
  validate?: (value: unknown) => string | undefined;
  isLoading?: boolean;
};

export type ApiLookupInputBaseProps<
  FState extends Record<string, any>,
  FPath extends NestedKeyOf<FState>,
> = SharedLookupInputProps<FState, FPath> & {
  sx?: SxProps<Theme>;
  onInputChange?: (arg0: string) => void;
  /** If true, this will filter locally rather than follow API-based filtering. */
  localFiltering?: boolean;
  disablePortal?: boolean;
};

/**
 * `GetOptFromValue` returns the first Option that contains the value provided.
 * If there is no match, `null` is returned.
 * If there should be a match but none is provided, it alerts `LogRocket` and returns `null`.
 */
export const GetOptFromValue = (v: any, options: LookupInputOption[]): LookupInputOption | null => {
  // Short-circuit if there is guaranteed to be no matches
  if (v === '' || v === null || options.length === 0) {
    return null;
  }
  const valueOpt = options.find((opt) => opt.value === v);
  if (v !== null && valueOpt === undefined) {
    LogRocket.error('InputLookup selected value did not match available options:', v);
  }
  return valueOpt === undefined ? null : valueOpt;
};

type LookupInputInputProps = {
  params: AutocompleteRenderInputParams;
  name: string;
  stateValue: string;
  hasError: boolean;
  helperText: string | undefined;
  isLoading: boolean;

  placeholder?: string;
  label?: string;
  tip?: string;
};
export const LookupInputInput = ({
  params,
  name,
  stateValue,
  hasError,
  helperText,
  isLoading,
  placeholder,
  label,
  tip,
}: LookupInputInputProps): ReactElement => {
  const labelWithTip =
    label === undefined ? null : (
      <Box display='flex' gap='0.25rem' alignItems='center'>
        {label}
        {tip !== undefined ? <InputTooltip tip={tip} /> : null}
      </Box>
    );
  return (
    <TextField
      {...params}
      name={name}
      value={stateValue}
      error={hasError}
      helperText={hasError ? helperText : ' '}
      placeholder={placeholder}
      label={labelWithTip}
      // Close outlined box + fix height if label is undefined
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <>
            {isLoading ? <LoadingSpinner color='inherit' size={20} wrapInBox={false} /> : null}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
    />
  );
};

/**
 * `LookupInputAutocompleteOption` is the component used when rendering an option inside
 * of a lookup.
 */
export const LookupInputAutocompleteOption = (
  props: HTMLAttributes<HTMLLIElement>,
  option: LookupInputOption,
): ReactElement => (
  <Box
    // 'props' handles all hover + click events + default css
    {...props}
    // Providing an explicit `value` as the key should guarantee unique-ness.
    // If every `value` is not unique, the user may experience lingering options rendered at unexpected times.
    key={option.value}
    component='li'
    display='flex'
    flexDirection='column'
    alignItems='start !important'
    padding='0rem'
    whiteSpace='nowrap'
  >
    <Typography>{option.label}</Typography>
    {(option.sublabels ?? [])
      .filter((sl) => sl !== '')
      .map((sublabel) => (
        <Typography key={randomUUID()} variant='body3'>
          {sublabel}
        </Typography>
      ))}
  </Box>
);

type QueryParams<T extends {}> = {
  queryParams: ListURLParams<T>;
};

/**
 * Given the form state and inputText, construct appropriate query parameters.
 *
 * If a form is initially loaded (not dirty), search for a match where possible.
 *
 * If a form is dirty, search against the user input text.
 *
 * @typeParam T - The entity type for proper filter/sort type checking. Must be explicitly provided.
 */
export const ConstructListQueryParams = <T extends {} = {}>(
  inputText: string,
  queryParams?: Partial<ListURLParams<T>>,
): QueryParams<T> => {
  const { page, perPage, sortBy, filterBy = [], columns, isDistinct } = queryParams ?? {};

  const trimmedInput = inputText.trim();
  const lookupFilter: FilterByOpts<T>[] =
    trimmedInput !== ''
      ? [{ operation: Enums.FilterOperation.Lookup, values: [trimmedInput] }]
      : [];

  const combinedFilterBy = [...lookupFilter, ...filterBy];

  return {
    queryParams: {
      page: page ?? 0,
      perPage: perPage ?? DEFAULT_LOOKUP_LENGTH,
      ...(combinedFilterBy.length > 0 && { filterBy: combinedFilterBy }),
      ...(sortBy !== undefined && { sortBy }),
      ...(columns !== undefined && { columns }),
      ...(isDistinct !== undefined && { isDistinct }),
    },
  };
};

/**
 * `AlertDupeOptions` will alert LogRocket if there are any duplicate
 * `value`s inside of options.
 */
export const AlertDupeOptions = (options: LookupInputOption[]): void => {
  // If there are duplicate `value`s, alert LogRocket as this will affect user experience.
  const optVals = options.map((o) => o.value);
  const dupeVals = optVals.filter((value, index, self) => self.indexOf(value) !== index);
  if (dupeVals.length !== 0) {
    LogRocket.error(
      'Duplicate option values found. This may show users unintended lookup values.',
      dupeVals,
    );
  }
};
